import { useState } from "react";
import { toast } from "sonner";
import { Camera, Trash, VideoCamera, Play } from "@phosphor-icons/react";
import { Section, NextBar } from "./Fields";
import { PHOTO_CATS, readAsDataURL, uid } from "./constants";

export default function PhotosStep({
  inspection, addPhotos, removePhoto, patch, onPrev, onNext,
}) {
  return (
    <Section title="Property photos" subtitle="Upload multiple photos, tagged by category.">
      <div className="grid md:grid-cols-4 gap-3 mb-6">
        {PHOTO_CATS.map((c) => (
          <label
            key={c}
            className="border border-dashed border-[#E5E7EB] hover:border-[#1E3A5F] rounded-md p-4 text-center cursor-pointer bg-white transition-colors"
            data-testid={`upload-${c.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Camera size={22} className="mx-auto text-[#1E3A5F] mb-2" weight="duotone" />
            <div className="text-xs font-semibold text-[#1E3A5F]">{c}</div>
            <div className="text-[10px] text-[#6B7280] uppercase tracking-widest mt-1">Click to upload</div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files, c)} />
          </label>
        ))}
      </div>

      {(inspection.photos || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {inspection.photos.map((p) => (
            <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
              <img src={p.data_url} alt={p.category} className="w-full h-32 object-cover rounded-sm border border-[#E5E7EB]" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1">
                {p.category}
              </div>
              <button onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-white text-[#991B1B] p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <VideoUploader inspection={inspection} patch={patch} />

      <NextBar onPrev={onPrev} onNext={onNext} testId="wizard-next-4" />
    </Section>
  );
}

function VideoUploader({ inspection, patch }) {
  const videos = inspection.videos || [];
  const isWalkAround = inspection.inspection_type === "Video Walk-Around";
  const [uploading, setUploading] = useState(false);

  const onUpload = async (files) => {
    setUploading(true);
    try {
      const list = [...videos];
      for (const f of Array.from(files)) {
        if (f.size > 50 * 1024 * 1024) {
          toast.error(`"${f.name}" is over 50 MB — please trim it before uploading.`);
          continue;
        }
        const data_url = await readAsDataURL(f);
        list.push({ id: uid(), label: f.name.replace(/\.[^.]+$/, ""), caption: "", data_url });
      }
      await patch({ videos: list });
      toast.success(`Added ${files.length} video(s)`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (vid) => {
    await patch({ videos: videos.filter((v) => v.id !== vid) });
  };

  const updateLabel = async (vid, label) => {
    await patch({ videos: videos.map((v) => (v.id === vid ? { ...v, label } : v)) });
  };

  return (
    <div className="mt-8 border-t border-[#E5E7EB] pt-6" data-testid="video-upload-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#16A34A] font-semibold">
            {isWalkAround ? "Walk-around videos — required" : "Videos (optional)"}
          </div>
          <div className="trent-heading text-lg font-bold text-[#1E3A5F]">
            {isWalkAround ? "Record a video walk-through of the property" : "Add room or feature videos"}
          </div>
        </div>
        <label className="trent-btn-primary cursor-pointer" data-testid="upload-video-btn">
          <VideoCamera size={16} weight="duotone" /> {uploading ? "Uploading…" : "Upload video"}
          <input type="file" accept="video/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
        </label>
      </div>

      {videos.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] rounded-md p-6 text-center text-sm text-[#6B7280]">
          {isWalkAround
            ? "No videos yet — at least one walk-through clip is recommended for this inspection type."
            : "No videos uploaded for this inspection."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="border border-[#E5E7EB] rounded-md overflow-hidden bg-black" data-testid={`video-${v.id}`}>
              <video src={v.data_url} controls className="w-full h-44 bg-black object-cover" preload="metadata">
                <track kind="captions" />
              </video>
              <div className="p-3 bg-white">
                <input
                  value={v.label || ""}
                  onChange={(e) => updateLabel(v.id, e.target.value)}
                  placeholder="Label (e.g. Living-room walk-through)"
                  className="trent-input text-sm"
                  data-testid={`video-label-${v.id}`}
                />
                <div className="flex justify-between items-center mt-2 text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <span className="inline-flex items-center gap-1">
                    <Play size={12} weight="fill" /> Inline player
                  </span>
                  <button
                    onClick={() => remove(v.id)}
                    className="text-[#991B1B] hover:underline"
                    data-testid={`video-remove-${v.id}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
